import { useState, useEffect, useRef, useCallback } from "react";
import { addLayerToggle } from "@/lib/mapLayers";
import { isOfficerLive, lastSeenAgo } from "@/lib/officerStatus";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Radio, Clock, ClipboardList, AlertTriangle, CheckCircle2, Eye, Plus, X, Trash2, Building2, Map, Key, Copy, ArrowLeft, ShieldAlert, User, MessageSquare, Download } from "lucide-react";
import SupportTicketForm from "@/components/app/SupportTicketForm";
import ProfileSection from "@/components/app/ProfileSection";
import ThemeToggle from "@/components/ThemeToggle";
import RiskManagement from "@/components/app/RiskManagement";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import DashboardBreadcrumb from "@/components/app/DashboardBreadcrumb";
import { useMotion } from "@/lib/safeMotion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useSiteOwnerData } from "@/hooks/useSiteOwnerData";
import PromoCodeRedeem from "@/components/app/PromoCodeRedeem";
import MapAddressSearch from "@/components/app/MapAddressSearch";
import IncidentCard from "@/components/app/IncidentCard";

const tabs = [
  { id: "live", label: "Live", icon: Radio },
  { id: "map", label: "Map", icon: Map },
  { id: "schedule", label: "Schedule", icon: Clock },
  { id: "log", label: "Log", icon: ClipboardList },
  { id: "incidents", label: "Incidents", icon: AlertTriangle },
  { id: "risks", label: "Risks", icon: ShieldAlert },
  { id: "sites", label: "My Sites", icon: Building2 },
  { id: "support", label: "Support", icon: MessageSquare },
  { id: "profile", label: "Profile", icon: User },
];

const SiteOwnerDashboard = () => {
  const { motion, AnimatePresence } = useMotion();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { sites, officers, checkIns, incidents, checkpoints, isLoading, addSite, removeSite, claimSite, addIncident } = useSiteOwnerData();

  const [activeTab, setActiveTab] = useState("live");
  const [incidentFilter, setIncidentFilter] = useState("all");
  const [activeSiteId, setActiveSiteId] = useState<string | "all">("all");
  const [showAddSite, setShowAddSite] = useState(false);
  const [showClaimSite, setShowClaimSite] = useState(false);
  const [newSite, setNewSite] = useState({ name: "", address: "" });
  const [inviteCode, setInviteCode] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [showAddIncidentModal, setShowAddIncidentModal] = useState(false);
  const [newIncident, setNewIncident] = useState({ title: "", description: "", severity: "low", category: "Other", siteId: "", officerId: "" });

  // Map refs
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const userAccuracyCircleRef = useRef<any>(null);
  const userLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const mapLoadedForTab = useRef<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Real-time clock
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // User geolocation — works globally, no hardcoded fallback
  useEffect(() => {
    // Smart fallback: use first known officer/checkpoint/site coordinate from DB
    const getSmartFallback = () => {
      const liveOfficer = officers.find(o => o.lat != null && o.lng != null);
      if (liveOfficer) return { lat: liveOfficer.lat!, lng: liveOfficer.lng! };
      const cp = checkpoints.find(c => c.lat != null && c.lng != null);
      if (cp) return { lat: cp.lat!, lng: cp.lng! };
      const site = sites.find(s => s.lat != null && s.lng != null);
      if (site) return { lat: site.lat!, lng: site.lng! };
      return { lat: 0, lng: 0 }; // world center if absolutely no data
    };

    if (!navigator.geolocation) {
      const fallback = getSmartFallback();
      setUserLocation(fallback);
      userLocationRef.current = fallback;
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        userLocationRef.current = loc;
        setUserLocation(loc);
        if (userMarkerRef.current) {
          userMarkerRef.current.setLatLng([loc.lat, loc.lng]);
        }
        if (userAccuracyCircleRef.current) {
          userAccuracyCircleRef.current.setLatLng([loc.lat, loc.lng]);
        }
      },
      () => {
        const fallback = getSmartFallback();
        userLocationRef.current = fallback;
        setUserLocation(fallback);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [officers, checkpoints, sites]);

  // Clean up stale officer statuses on mount
  useEffect(() => {
    supabase.functions.invoke("cleanup-stale-officers").catch(() => {});
  }, []);

  // Filter data by active site
  const filteredOfficers = activeSiteId === "all" ? officers : officers.filter(o => o.site_id === activeSiteId);
  const filteredCheckpoints = activeSiteId === "all" ? checkpoints : checkpoints.filter(cp => cp.site_id === activeSiteId);
  const filteredCheckIns = activeSiteId === "all" ? checkIns : checkIns.filter(c => c.site_id === activeSiteId);
  const filteredIncidents = incidents.filter(inc => {
    const siteMatch = activeSiteId === "all" || inc.site_id === activeSiteId;
    const filterMatch = incidentFilter === "all" || inc.severity === incidentFilter || (incidentFilter === "Escalated" && inc.escalated);
    return siteMatch && filterMatch;
  });

  // Coverage calculation
  const checkedCheckpointIds = new Set(filteredCheckIns.map(c => c.checkpoint_id));
  const doneCount = filteredCheckpoints.filter(cp => checkedCheckpointIds.has(cp.id)).length;
  const coverage = filteredCheckpoints.length > 0 ? Math.round((doneCount / filteredCheckpoints.length) * 100) : 0;

  // Helper functions (must be defined before initMap which uses them)
  const getSiteName = (siteId: string | null) => sites.find(s => s.id === siteId)?.name || "";
  const getOfficerName = (officerId: string) => officers.find(o => o.id === officerId)?.name || "Unknown";

  // Map initialization
  const initMap = useCallback(async (container: HTMLDivElement) => {
    if (!container) return;
    const key = `${activeTab}-${activeSiteId}`;
    if (mapLoadedForTab.current === key && mapInstanceRef.current) return;
    mapLoadedForTab.current = key;

    const L = await import("leaflet");
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
      await new Promise(r => setTimeout(r, 300));
    }

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    container.innerHTML = "";

    const loc = userLocationRef.current || { lat: 0, lng: 0 };
    const map = L.map(container, { zoomControl: true }).setView([loc.lat, loc.lng], 16);
    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);
    addLayerToggle(L, map);

    const bounds: [number, number][] = [];
    if (loc.lat !== 0 || loc.lng !== 0) bounds.push([loc.lat, loc.lng]);

    // YOU marker
    const youIcon = L.divIcon({
      html: '<div style="width:24px;height:24px;border-radius:50%;background:#3B82F6;border:3px solid white;box-shadow:0 0 12px rgba(59,130,246,0.6);animation:pulse 2s infinite;"></div>',
      iconSize: [24, 24], iconAnchor: [12, 12], className: '',
    });
    const youMarker = L.marker([loc.lat, loc.lng], { icon: youIcon }).addTo(map)
      .bindPopup(`<div style="color:#000"><b>&#9679; YOU</b><br/><span style="font-family:monospace;font-size:11px">${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}</span></div>`);
    userMarkerRef.current = youMarker;

    const accCircle = L.circle([loc.lat, loc.lng], {
      radius: 30, color: '#3B82F6', fillColor: 'rgba(59,130,246,0.1)', fillOpacity: 0.3, weight: 1, dashArray: '4 4',
    }).addTo(map);
    userAccuracyCircleRef.current = accCircle;

    // Officers — use REAL GPS coordinates from DB
    const liveOfficers = filteredOfficers.filter(o => isOfficerLive(o as any));
    liveOfficers.forEach((officer) => {
      const oLat = officer.lat;
      const oLng = officer.lng;
      if (oLat == null || oLng == null) return;
      bounds.push([oLat, oLng]);
      const icon = L.divIcon({
        html: `<div style="position:relative;width:20px;height:20px;border-radius:50%;background:#10B981;border:2px solid white;box-shadow:0 0 10px rgba(16,185,129,0.5);"><div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid rgba(16,185,129,0.3);animation:pulse 2s infinite;"></div></div>`,
        iconSize: [20, 20], iconAnchor: [10, 10], className: '',
      });
      L.marker([oLat, oLng], { icon }).addTo(map)
        .bindPopup(`<div style="color:#000"><b>${officer.name}</b><br/>${getSiteName(officer.site_id)}<br/>Check-ins: ${officer.checkins}</div>`)
        .bindTooltip(officer.name, { permanent: true, direction: "top", offset: [0, -12], className: "officer-name-label" });
    });

    filteredOfficers.filter(o => !isOfficerLive(o as any)).forEach((officer) => {
      if (officer.lat == null || officer.lng == null) return;
      bounds.push([officer.lat, officer.lng]);
      const icon = L.divIcon({
        html: '<div style="width:16px;height:16px;border-radius:50%;background:#6B7280;border:2px solid white;opacity:0.7;"></div>',
        iconSize: [16, 16], iconAnchor: [8, 8], className: '',
      });
      L.marker([officer.lat, officer.lng], { icon }).addTo(map)
        .bindPopup(`<div style="color:#000"><b>${officer.name}</b> (Offline)<br/>${getSiteName(officer.site_id)}</div>`)
        .bindTooltip(officer.name, { permanent: true, direction: "top", offset: [0, -10], className: "officer-name-label" });
    });

    // Checkpoints — use REAL coordinates from DB
    filteredCheckpoints.forEach((cp) => {
      const cpLat = cp.lat;
      const cpLng = cp.lng;
      if (cpLat == null || cpLng == null) return;
      bounds.push([cpLat, cpLng]);
      const done = checkedCheckpointIds.has(cp.id);
      const icon = L.divIcon({
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${done ? '#F0B429' : '#EF4444'};border:2px solid white;box-shadow:0 0 6px ${done ? 'rgba(240,180,41,0.4)' : 'rgba(239,68,68,0.4)'};"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7], className: '',
      });
      L.marker([cpLat, cpLng], { icon }).addTo(map)
        .bindPopup(`<div style="color:#000"><b>${cp.name}</b><br/>${cp.zone || ""}${done ? '<br/><b style="color:#10B981">Checked ✓</b>' : '<br/><i>Not checked today</i>'}</div>`);
      if (cp.method === "gps" && (cp.radius || 50) > 0) {
        L.circle([cpLat, cpLng], { radius: cp.radius || 50, color: done ? '#F0B429' : '#EF4444', fillColor: done ? 'rgba(240,180,41,0.08)' : 'rgba(239,68,68,0.08)', fillOpacity: 0.3, weight: 1, dashArray: '4 4' }).addTo(map);
      }
    });

    // Auto-fit map to show all markers globally
    if (bounds.length > 1) {
      try { map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 }); } catch {}
    } else if (bounds.length === 0) {
      map.setView([0, 0], 2); // World view if no data
    }

    setTimeout(() => map.invalidateSize(), 200);
  }, [activeSiteId, activeTab, filteredOfficers, filteredCheckpoints, checkedCheckpointIds]);

  // Clean up map when leaving map tab
  useEffect(() => {
    if (activeTab !== "map") {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        userMarkerRef.current = null;
        userAccuracyCircleRef.current = null;
        mapLoadedForTab.current = null;
      }
    }
  }, [activeTab]);

  const mapCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node && activeTab === "map") {
      (mapRef as any).current = node;
      setTimeout(() => initMap(node), 100);
    }
  }, [activeTab, initMap]);

  const handleAddSite = async () => {
    if (!newSite.name.trim()) { toast.error("Site name is required"); return; }
    try {
      await addSite.mutateAsync({ name: newSite.name, address: newSite.address });
      setNewSite({ name: "", address: "" });
      setShowAddSite(false);
      toast.success("Site added successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to add site");
    }
  };

  const handleClaimSite = async () => {
    if (!inviteCode.trim()) { toast.error("Please enter an invite code"); return; }
    setClaiming(true);
    try {
      await claimSite.mutateAsync(inviteCode.trim());
      setInviteCode("");
      setShowClaimSite(false);
      toast.success("Site linked to your dashboard!");
    } catch (err: any) {
      toast.error(err.message || "Invalid invite code");
    }
    setClaiming(false);
  };

  const handleRemoveSite = async (siteId: string) => {
    try {
      await removeSite.mutateAsync(siteId);
      if (activeSiteId === siteId) setActiveSiteId("all");
      toast.success("Site removed");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove site");
    }
  };

  const handleAddIncident = async () => {
    if (!newIncident.title.trim()) { toast.error("Title is required"); return; }
    if (!newIncident.siteId) { toast.error("Please select a site"); return; }
    try {
      await addIncident.mutateAsync({
        title: newIncident.title.trim(),
        description: newIncident.description.trim() || null,
        severity: newIncident.severity,
        category: newIncident.category || null,
        site_id: newIncident.siteId,
        officer_id: newIncident.officerId || null,
      });
      setNewIncident({ title: "", description: "", severity: "low", category: "Other", siteId: "", officerId: "" });
      setShowAddIncidentModal(false);
      toast.success("Incident reported successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to report incident");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-8 h-8 text-[hsl(var(--app-gold))] mx-auto mb-3 animate-pulse" />
          <p className="text-sm text-[hsl(var(--app-text-muted))]">Loading your sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-bg font-outfit pb-16 md:pb-0">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--app-border-subtle))] app-surface-1 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate("/")} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors shrink-0" title="Back to website">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <img src={secureflexLogo} alt="SecureFlex Logo" className="h-12 w-12 object-contain shrink-0" />
            <span className="font-bebas text-base sm:text-lg tracking-wider text-[hsl(var(--app-text-primary))]">SECUREFLEX</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--app-blue)/0.15)] text-[hsl(var(--app-blue))] whitespace-nowrap">SITE OPERATOR</span>
            <span className="font-mono-data text-[10px] text-[hsl(var(--app-gold))] hidden sm:inline">{currentTime.toLocaleTimeString()}</span>
            <ThemeToggle />
            <button onClick={async () => { await signOut(); navigate("/app"); }} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] flex items-center text-xs shrink-0">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <DashboardBreadcrumb role="Site Operator" activeTab={tabs.find(t => t.id === activeTab)?.label || activeTab} />

      {/* Site selector */}
      {sites.length > 0 && (
        <div className="border-b border-[hsl(var(--app-border-subtle))] app-surface-1 px-4 py-2 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveSiteId("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              activeSiteId === "all"
                ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]"
                : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
            }`}
          >
            All Sites
          </button>
          {sites.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSiteId(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeSiteId === s.id
                  ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]"
                  : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Tabs - desktop */}
      <div className="hidden md:block border-b border-[hsl(var(--app-border-subtle))] app-surface-1">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-x-0.5">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === tab.id ? "border-[hsl(var(--app-gold))] text-[hsl(var(--app-gold))]" : "border-transparent text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))]"
                }`}><Icon className="w-3.5 h-3.5" /> {tab.label}</button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto p-4">
        {sites.length === 0 && activeTab !== "sites" ? (
          <div className="text-center py-16">
            <Building2 className="w-12 h-12 text-[hsl(var(--app-text-muted))] mx-auto mb-4" />
            <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))] mb-2">NO SITES YET</h2>
            <p className="text-sm text-[hsl(var(--app-text-muted))] mb-6">Link a site using an invite code from your security company, or add your own</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setActiveTab("sites"); setShowClaimSite(true); }} className="px-6 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center gap-2">
                <Key className="w-4 h-4" /> Enter Invite Code
              </button>
              <button onClick={() => setActiveTab("sites")} className="px-6 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-all">
                Add Your Own Site
              </button>
            </div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>

              {activeTab === "live" && (
                <div className="space-y-4">
                  <div className="app-card rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Today's Coverage</p>
                      <span className="font-bebas text-4xl text-[hsl(var(--app-gold))]">{coverage}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[hsl(var(--app-border-subtle))] overflow-hidden">
                      <div className="h-full rounded-full bg-[hsl(var(--app-gold))] transition-all" style={{ width: `${coverage}%` }} />
                    </div>
                    <p className="text-xs text-[hsl(var(--app-text-muted))] mt-2">{doneCount} of {filteredCheckpoints.length} checkpoints visited today</p>
                  </div>

                  <h3 className="font-bebas text-lg text-[hsl(var(--app-text-primary))] flex items-center gap-2">
                    OFFICER STATUS
                    <span className="text-xs font-outfit font-normal text-[hsl(var(--app-text-muted))] ml-auto">
                      {filteredOfficers.filter(o => isOfficerLive(o as any)).length} / {filteredOfficers.length} active
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filteredOfficers.map(officer => {
                      const isLive = isOfficerLive(officer as any);
                      const lastSeen = (officer as any).last_seen ? new Date((officer as any).last_seen) : null;
                      const lastSeenAgo = lastSeen ? (() => {
                        const diffMs = Date.now() - lastSeen.getTime();
                        const mins = Math.floor(diffMs / 60000);
                        if (mins < 1) return "Just now";
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })() : null;

                      return (
                        <div key={officer.id} className={`app-card rounded-xl p-3 border-l-4 ${isLive ? "border-l-[hsl(var(--app-green))]" : "border-l-[hsl(var(--app-border-subtle))]"}`}>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${isLive ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-muted))]"}`}>
                                {officer.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                              </div>
                              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[hsl(var(--app-surface-1))] ${isLive ? "bg-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-text-muted))]"}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[hsl(var(--app-text-primary))] truncate">{officer.name}</p>
                              <div className="flex items-center gap-1.5">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isLive ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-muted))]"}`}>
                                  {isLive ? "â— LIVE" : "○  OFFLINE"}
                                </span>
                                <span className="text-[9px] text-[hsl(var(--app-text-muted))] truncate">{getSiteName(officer.site_id)}</span>
                              </div>
                              {lastSeenAgo && (
                                <p className="text-[9px] text-[hsl(var(--app-text-muted))] mt-0.5 flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5" /> Last seen: {lastSeenAgo}
                                  {lastSeen && <span className="font-mono-data">({lastSeen.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})</span>}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {filteredOfficers.length === 0 && (
                      <p className="text-sm text-[hsl(var(--app-text-muted))] col-span-2 text-center py-6">No officers assigned to your sites</p>
                    )}
                  </div>

                  <h3 className="font-bebas text-lg text-[hsl(var(--app-text-primary))]">CHECKPOINTS ({filteredCheckpoints.length})</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredCheckpoints.map((cp) => {
                      const isChecked = checkedCheckpointIds.has(cp.id);
                      const lastCheckIn = filteredCheckIns.find(c => c.checkpoint_id === cp.id);
                      return (
                        <div key={cp.id} className={`app-card rounded-xl p-4 border-l-4 ${isChecked ? "border-l-[hsl(var(--app-green))]" : "border-l-[hsl(var(--app-border-active))]"}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{cp.name}</p>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${isChecked ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"}`}>{isChecked ? "OK" : "Pending"}</span>
                          </div>
                          <p className="text-xs text-[hsl(var(--app-text-muted))]">{cp.zone || "—"} · {getSiteName(cp.site_id)}</p>
                          {isChecked && lastCheckIn ? (
                            <p className="text-xs text-[hsl(var(--app-text-secondary))] mt-2 flex items-center gap-1">{getOfficerName(lastCheckIn.officer_id)} · {new Date(lastCheckIn.checked_at).toLocaleTimeString()} · <Eye className="w-3 h-3" /> {lastCheckIn.method || "GPS"}</p>
                          ) : (
                            <p className="text-xs text-[hsl(var(--app-text-muted))] mt-2 italic">Not yet visited today</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === "map" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">LIVE MAP</h2>
                    <div className="flex items-center gap-4 text-[10px] text-[hsl(var(--app-text-muted))]">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /> You</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" /> On Duty</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#6B7280]" /> Offline</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#F0B429]" /> Checkpoint</span>
                    </div>
                  </div>
                  <MapAddressSearch mapInstance={mapInstanceRef.current} />
                  <div
                    ref={mapCallbackRef}
                    className="w-full rounded-xl border border-[hsl(var(--app-border-subtle))] overflow-hidden bg-[hsl(var(--app-surface-2))]"
                    style={{ height: "500px" }}
                  />
                  {/* Officer sidebar */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filteredOfficers.map(officer => (
                      <div key={officer.id} className="app-card rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${isOfficerLive(officer as any) ? "bg-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-active))]"}`} />
                          <div>
                            <p className="text-xs font-semibold text-[hsl(var(--app-text-primary))]">{officer.name}</p>
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))]">{getSiteName(officer.site_id)}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-[hsl(var(--app-text-muted))]">{officer.checkins} check-ins</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "schedule" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">PATROL SCHEDULE</h2>
                    <button onClick={() => { import("@/lib/pdfExport").then(m => m.exportLogPdf(filteredCheckIns.map(ci => { const cp = checkpoints.find(c => c.id === ci.checkpoint_id); const d = new Date(ci.checked_at); return { date: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }), time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), checkpoint: cp?.name || "—", site: getSiteName(ci.site_id), officer: getOfficerName(ci.officer_id), method: ci.method || "—", scheduled: "—", onTime: ci.on_time !== false, variance: ci.variance_minutes ? `${ci.variance_minutes}m` : null }; }))); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors">
                      <Download className="w-3.5 h-3.5" /> Export PDF
                    </button>
                  </div>
                  {filteredCheckIns.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--app-text-muted))] text-center py-10">No check-ins recorded today</p>
                  ) : (
                    <div className="app-card rounded-xl overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[hsl(var(--app-border-subtle))]">
                            {["Time", "Checkpoint", "Site", "Officer", "Method", "On Time"].map((h) => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCheckIns.map((ci, i) => {
                            const cp = checkpoints.find(c => c.id === ci.checkpoint_id);
                            return (
                              <tr key={ci.id} className={`border-b border-[hsl(var(--app-border-subtle))] ${i % 2 === 0 ? "bg-[hsl(var(--app-surface-2)/0.5)]" : ""}`}>
                                <td className="px-4 py-3 font-mono-data text-[hsl(var(--app-gold))]">{new Date(ci.checked_at).toLocaleTimeString()}</td>
                                <td className="px-4 py-3 text-[hsl(var(--app-text-primary))]">{cp?.name || "—"}</td>
                                <td className="px-4 py-3 text-[hsl(var(--app-text-secondary))]">{getSiteName(ci.site_id)}</td>
                                <td className="px-4 py-3 text-[hsl(var(--app-text-secondary))]">{getOfficerName(ci.officer_id)}</td>
                                <td className="px-4 py-3">{ci.method ? <span className="px-2 py-1 rounded-full text-xs bg-[hsl(var(--app-purple)/0.15)] text-[hsl(var(--app-purple))] inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {ci.method}</span> : "—"}</td>
                                <td className="px-4 py-3">{ci.on_time ? <span className="text-[hsl(var(--app-green))]">✓</span> : <span className="text-[hsl(var(--app-red))]">Late</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "log" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">CHECK-IN LOG</h2>
                    <button onClick={() => { import("@/lib/pdfExport").then(m => m.exportLogPdf(filteredCheckIns.map(ci => { const cp = checkpoints.find(c => c.id === ci.checkpoint_id); const d = new Date(ci.checked_at); return { date: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }), time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), checkpoint: cp?.name || "—", site: getSiteName(ci.site_id), officer: getOfficerName(ci.officer_id), method: ci.method || "—", scheduled: "—", onTime: ci.on_time !== false, variance: ci.variance_minutes ? `${ci.variance_minutes}m` : null }; }))); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors">
                      <Download className="w-3.5 h-3.5" /> Export PDF
                    </button>
                  </div>
                  {filteredCheckIns.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--app-text-muted))] text-center py-10">No check-ins today</p>
                  ) : (
                    filteredCheckIns.map((ci) => {
                      const cp = checkpoints.find(c => c.id === ci.checkpoint_id);
                      return (
                        <div key={ci.id} className="app-card rounded-xl p-4">
                          <div className="flex items-baseline gap-3 mb-1">
                            <span className="font-mono-data text-[hsl(var(--app-gold))] text-sm font-semibold">{new Date(ci.checked_at).toLocaleTimeString()}</span>
                            <span className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{cp?.name || "Unknown"}</span>
                          </div>
                          <p className="text-xs text-[hsl(var(--app-text-muted))]">{getOfficerName(ci.officer_id)} · {getSiteName(ci.site_id)}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`text-xs inline-flex items-center gap-1 ${ci.on_time ? "text-[hsl(var(--app-green))]" : "text-[hsl(var(--app-red))]"}`}>
                              <CheckCircle2 className="w-3 h-3" /> {ci.on_time ? "On Time" : `Late (${ci.variance_minutes}m)`}
                            </span>
                            {ci.method && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--app-purple)/0.15)] text-[hsl(var(--app-purple))] inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {ci.method}</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {activeTab === "incidents" && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">INCIDENTS</h2>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { import("@/lib/pdfExport").then(m => m.exportIncidentsPdf(filteredIncidents.map(inc => ({ date: new Date(inc.created_at).toLocaleDateString(), time: new Date(inc.created_at).toLocaleTimeString(), title: inc.title, severity: inc.severity, category: inc.category || "Other", officer: getOfficerName(inc.officer_id), site: getSiteName(inc.site_id), escalated: inc.escalated || false, resolved: inc.resolved || false })))); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors">
                        <Download className="w-3.5 h-3.5" /> Export PDF
                      </button>
                      <button onClick={() => setShowAddIncidentModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-xs font-semibold hover:opacity-90 transition-opacity">
                        <Plus className="w-3.5 h-3.5" /> Report Incident
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {["all", "Low", "Medium", "High", "Critical", "Escalated"].map((f) => (
                      <button key={f} onClick={() => setIncidentFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        incidentFilter === f ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]" : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                      }`}>{f === "all" ? "All" : f}</button>
                    ))}
                  </div>
                  {filteredIncidents.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--app-text-muted))] text-center py-10">No incidents reported</p>
                  ) : (
                    filteredIncidents.map((inc) => {
                      const created = new Date(inc.created_at);
                      return (
                        <IncidentCard
                          key={inc.id}
                          incident={{
                            id: inc.id,
                            title: inc.title,
                            description: inc.description || "",
                            severity: inc.severity,
                            category: inc.category || "Other",
                            time: created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                            date: created.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
                            officer: getOfficerName(inc.officer_id),
                            site: getSiteName(inc.site_id),
                            site_id: inc.site_id,
                            escalated: inc.escalated || false,
                            resolved: inc.resolved || false,
                            hasVoiceNote: !!inc.audio_url,
                            voiceDuration: 0,
                            audio_url: inc.audio_url,
                            photo_url: inc.photo_url,
                            ai_analysis: inc.ai_analysis,
                            resolution_notes: (inc as any).resolution_notes || null,
                          }}
                          canResolve={false}
                        />
                      );
                    })
                  )}

                  {/* Add Incident Modal */}
                  {showAddIncidentModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                      <div className="app-card rounded-2xl p-6 w-full max-w-md mx-4 space-y-4 relative max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setShowAddIncidentModal(false)} className="absolute top-4 right-4 text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"><X className="w-5 h-5" /></button>
                        <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">REPORT INCIDENT</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Title *</label>
                            <input value={newIncident.title} onChange={(e) => setNewIncident(p => ({ ...p, title: e.target.value }))} placeholder="Brief incident title" className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Description</label>
                            <textarea value={newIncident.description} onChange={(e) => setNewIncident(p => ({ ...p, description: e.target.value }))} placeholder="Describe the incident..." rows={3} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm resize-none" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Severity *</label>
                              <select value={newIncident.severity} onChange={(e) => setNewIncident(p => ({ ...p, severity: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Category</label>
                              <select value={newIncident.category} onChange={(e) => setNewIncident(p => ({ ...p, category: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                                <option value="Theft">Theft</option>
                                <option value="Trespassing">Trespassing</option>
                                <option value="Vandalism">Vandalism</option>
                                <option value="Fire">Fire</option>
                                <option value="Medical">Medical</option>
                                <option value="Suspicious Activity">Suspicious Activity</option>
                                <option value="Equipment Failure">Equipment Failure</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Site *</label>
                            <select value={newIncident.siteId} onChange={(e) => setNewIncident(p => ({ ...p, siteId: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                              <option value="">Select site</option>
                              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Officer (optional)</label>
                            <select value={newIncident.officerId} onChange={(e) => setNewIncident(p => ({ ...p, officerId: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                              <option value="">Select officer</option>
                              {officers.filter(o => !newIncident.siteId || o.site_id === newIncident.siteId).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => setShowAddIncidentModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] text-sm hover:bg-[hsl(var(--app-surface-2))]">Cancel</button>
                          <button onClick={handleAddIncident} disabled={addIncident.isPending} className="flex-1 px-4 py-2 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                            {addIncident.isPending ? "Submitting..." : "Report Incident"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "risks" && (
                <RiskManagement
                  sites={sites.map(s => ({ id: s.id, name: s.name }))}
                  mode="site_owner"
                  siteIds={sites.map(s => s.id)}
                />
              )}

              {activeTab === "sites" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">MY SITES</h2>
                    <div className="flex gap-2">
                      <button onClick={() => setShowClaimSite(true)} className="px-4 py-2 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-sm font-semibold text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-all flex items-center gap-2">
                        <Key className="w-4 h-4" /> Enter Code
                      </button>
                      <button onClick={() => setShowAddSite(true)} className="px-4 py-2 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Add Site
                      </button>
                    </div>
                  </div>

                  {/* Claim Site with Code */}
                  <AnimatePresence>
                    {showClaimSite && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="app-card rounded-xl p-5 border border-[hsl(var(--app-gold)/0.3)]">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">Link Site with Invite Code</h3>
                            <p className="text-xs text-[hsl(var(--app-text-muted))] mt-0.5">Enter the 6-character code from your security company</p>
                          </div>
                          <button onClick={() => setShowClaimSite(false)} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))]"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex gap-3">
                          <input
                            value={inviteCode}
                            onChange={e => setInviteCode(e.target.value.toUpperCase().slice(0, 6))}
                            placeholder="e.g. A3B7K9"
                            maxLength={6}
                            className="flex-1 px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-center text-lg font-mono-data font-bold text-[hsl(var(--app-gold))] tracking-[0.2em] placeholder:text-[hsl(var(--app-text-muted))] placeholder:text-sm placeholder:font-normal placeholder:tracking-normal outline-none focus:border-[hsl(var(--app-gold))]"
                          />
                          <button onClick={handleClaimSite} disabled={claiming || inviteCode.length < 4} className="px-6 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50 flex items-center gap-1.5">
                            {claiming ? "Linking..." : <><CheckCircle2 className="w-4 h-4" /> Link</>}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Add Site Form */}
                  <AnimatePresence>
                    {showAddSite && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="app-card rounded-xl p-5 border border-[hsl(var(--app-gold)/0.3)]">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">Add New Site</h3>
                          <button onClick={() => setShowAddSite(false)} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))]"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="space-y-3">
                          <input value={newSite.name} onChange={e => setNewSite({ ...newSite, name: e.target.value })} placeholder="Site name" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                          <input value={newSite.address} onChange={e => setNewSite({ ...newSite, address: e.target.value })} placeholder="Address" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                          <button onClick={handleAddSite} className="w-full py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Add Site</button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Sites list */}
                  {sites.length === 0 ? (
                    <div className="text-center py-10">
                      <Building2 className="w-10 h-10 text-[hsl(var(--app-text-muted))] mx-auto mb-3" />
                      <p className="text-sm text-[hsl(var(--app-text-muted))]">No sites yet. Use an invite code or add your own.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {sites.map(site => {
                        const siteOfficers = officers.filter(o => o.site_id === site.id);
                        const siteCheckpoints = checkpoints.filter(cp => cp.site_id === site.id);
                        return (
                          <div key={site.id} className="app-card rounded-xl p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="text-base font-semibold text-[hsl(var(--app-text-primary))]">{site.name}</p>
                                {site.address && <p className="text-xs text-[hsl(var(--app-text-muted))]">{site.address}</p>}
                              </div>
                              <button onClick={() => handleRemoveSite(site.id)} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-red))] hover:bg-[hsl(var(--app-red)/0.1)] transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex gap-4 text-xs text-[hsl(var(--app-text-muted))]">
                              <span>{siteOfficers.length} officer{siteOfficers.length !== 1 ? "s" : ""}</span>
                              <span>{siteCheckpoints.length} checkpoint{siteCheckpoints.length !== 1 ? "s" : ""}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "support" && <SupportTicketForm />}

              {activeTab === "profile" && (
                <div className="space-y-4">
                  <ProfileSection />
                  <PromoCodeRedeem />
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        )}
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden border-t border-[hsl(var(--app-border-subtle))] app-surface-1 z-40 backdrop-blur-sm">
        <div className="grid grid-cols-5 gap-0">
          {tabs.slice(0, 5).map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-2.5 flex flex-col items-center gap-0.5 text-[9px] font-semibold transition-colors ${
                activeTab === tab.id ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"
              }`}><Icon className="w-4 h-4" />{tab.label}</button>
            );
          })}
        </div>
        <div className="grid grid-cols-4 gap-0 border-t border-[hsl(var(--app-border-subtle)/0.5)]">
          {tabs.slice(5).map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-2.5 flex flex-col items-center gap-0.5 text-[9px] font-semibold transition-colors ${
                activeTab === tab.id ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"
              }`}><Icon className="w-4 h-4" />{tab.label}</button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default SiteOwnerDashboard;
